import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Loader2, RefreshCw, TrendingUp } from "lucide-react";

const WINDOW_OPTIONS = [
  { value: 1, label: "Son 1 saat" },
  { value: 6, label: "Son 6 saat" },
  { value: 24, label: "Son 24 saat" },
  { value: 24 * 7, label: "Son 7 gün" },
  { value: 24 * 30, label: "Son 30 gün" },
];

const EVENT_LABEL = {
  diagnostic_started: "Tanılama Başlatıldı",
  diagnostic_insufficient_package: "İçerik Yetersiz",
  diagnostic_completed: "Tanılama Tamamlandı",
  registration_handoff_started: "Kayıt Yönlendirmesi",
  diagnostic_claimed: "Tanılama Talep Edildi",
};

function fmtTs(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("tr-TR"); } catch { return iso; }
}

function fmtPct(v) {
  if (v === null || v === undefined) return "—";
  return `${(v * 100).toFixed(1)}%`;
}

function StatCard({ label, value, hint, testid }) {
  return (
    <div className="rounded-2xl border border-zinc-200 bg-white p-5" data-testid={testid}>
      <div className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">{label}</div>
      <div className="mt-2 font-display text-3xl font-bold text-zinc-900">
        {value === null || value === undefined ? "—" : value}
      </div>
      {hint && <div className="mt-1 text-xs text-zinc-500">{hint}</div>}
    </div>
  );
}

export default function AdminSayisalFunnel() {
  const [hours, setHours] = useState(24);
  const [summary, setSummary] = useState(null);
  const [recent, setRecent] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [err, setErr] = useState(null);
  const [filterEvent, setFilterEvent] = useState("");
  const [filterTier, setFilterTier] = useState("");

  async function load() {
    setRefreshing(true);
    setErr(null);
    try {
      const params = new URLSearchParams();
      params.set("limit", "100");
      if (filterEvent) params.set("event_type", filterEvent);
      if (filterTier) params.set("tier", filterTier);
      const [s, r] = await Promise.all([
        api.get(`/admin/sayisal/funnel/summary?hours=${hours}`).then((x) => x.data),
        api.get(`/admin/sayisal/funnel/recent?${params.toString()}`).then((x) => x.data),
      ]);
      setSummary(s);
      setRecent(r);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setRefreshing(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hours, filterEvent, filterTier]);

  const stage = summary?.stage_counts || {};
  const byTier = summary?.by_tier_event || {};
  const conv = summary?.conversion || {};
  const events = recent?.events || [];
  const allowedEvents = recent?.allowed_event_types || Object.keys(EVENT_LABEL);

  return (
    <AppShell variant="admin">
      <div className="max-w-6xl mx-auto p-5 lg:p-10" data-testid="admin-sayisal-funnel">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="text-xs font-semibold text-[#1E3A8A] uppercase tracking-wider">Sayısal · Huni</div>
            <h1 className="mt-2 font-display text-3xl font-bold tracking-tight text-zinc-900">
              Tanılama Hunisi
            </h1>
            <p className="mt-2 text-zinc-600 max-w-2xl">
              Sayısal Track B huni olayları — kimlik-taşımayan opak <span className="font-mono text-xs">owner_key</span>
              üzerinden aşama sayaçları, tier dağılımı ve dönüşüm oranları.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={hours}
              onChange={(e) => setHours(Number(e.target.value))}
              className="h-9 px-3 rounded-md border border-zinc-300 bg-white text-sm text-zinc-900"
              data-testid="funnel-window"
            >
              {WINDOW_OPTIONS.map((w) => (<option key={w.value} value={w.value}>{w.label}</option>))}
            </select>
            <button
              type="button" onClick={load} disabled={refreshing}
              className="h-9 px-3 rounded-md border border-zinc-300 bg-white text-sm text-zinc-900 hover:bg-zinc-50 disabled:opacity-50 inline-flex items-center gap-1.5"
              data-testid="funnel-refresh"
            >
              {refreshing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              Yenile
            </button>
          </div>
        </div>

        {err && <div className="mt-6 rounded-md bg-rose-50 border border-rose-200 p-3 text-sm text-rose-800">{err}</div>}

        {!summary && !err && (
          <div className="mt-10 text-center text-zinc-500"><Loader2 className="w-4 h-4 animate-spin mx-auto" /></div>
        )}

        {summary && (
          <>
            <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard label="Toplam Olay" value={summary.total_events} testid="stat-total-events" />
              <StatCard label="Benzersiz Öğrenci Anahtarı" value={summary.unique_owner_keys} testid="stat-unique-owners" />
              <StatCard label="Benzersiz Oturum" value={summary.unique_sessions} testid="stat-unique-sessions" />
              <StatCard label="Talep/Tamamlama" value={fmtPct(conv.claimed_over_completed)} hint="claimed / completed" testid="stat-conv-claim" />
            </div>

            <div className="mt-8 rounded-2xl border border-zinc-200 bg-white p-5" data-testid="funnel-stages">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-[#1E3A8A]" />
                <div className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">Huni Aşamaları</div>
              </div>
              <div className="mt-4 grid grid-cols-2 lg:grid-cols-5 gap-3">
                {[
                  ["diagnostic_started", stage.diagnostic_started, "Başlatıldı"],
                  ["diagnostic_insufficient_package", stage.diagnostic_insufficient_package, "İçerik Yetersiz"],
                  ["diagnostic_completed", stage.diagnostic_completed, "Tamamlandı"],
                  ["registration_handoff_started", stage.registration_handoff_started, "Kayıt Yönlendirme"],
                  ["diagnostic_claimed", stage.diagnostic_claimed, "Talep Edildi"],
                ].map(([k, v, label]) => (
                  <div key={k} className="rounded-lg border border-zinc-100 bg-zinc-50 p-3" data-testid={`stage-${k}`}>
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
                    <div className="mt-1 font-display text-2xl font-bold text-zinc-900">{v ?? 0}</div>
                  </div>
                ))}
              </div>
              <div className="mt-4 grid sm:grid-cols-3 gap-3 text-xs">
                <div><span className="text-zinc-500">completed / started:</span> <strong>{fmtPct(conv.completed_over_started)}</strong></div>
                <div><span className="text-zinc-500">handoff / completed:</span> <strong>{fmtPct(conv.handoff_over_completed)}</strong></div>
                <div><span className="text-zinc-500">claimed / completed:</span> <strong>{fmtPct(conv.claimed_over_completed)}</strong></div>
              </div>
            </div>

            <div className="mt-8 rounded-2xl border border-zinc-200 bg-white p-5" data-testid="funnel-by-tier">
              <div className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">Tier × Olay</div>
              {Object.keys(byTier).length === 0 ? (
                <div className="mt-3 text-xs text-zinc-500">Kayıt yok.</div>
              ) : (
                <table className="mt-3 w-full text-sm">
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500 border-b border-zinc-100">
                      <th className="py-1.5 font-semibold">Tier</th>
                      {allowedEvents.map((e) => (
                        <th key={e} className="py-1.5 font-semibold text-right">{EVENT_LABEL[e] || e}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(byTier).map(([tier, counts]) => (
                      <tr key={tier} className="border-b border-zinc-50 last:border-0">
                        <td className="py-1.5 font-mono text-xs text-zinc-700">{tier}</td>
                        {allowedEvents.map((e) => (
                          <td key={e} className="py-1.5 text-right text-zinc-900">{counts[e] || 0}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="mt-8 rounded-2xl border border-zinc-200 bg-white overflow-hidden" data-testid="funnel-recent">
              <div className="px-5 py-3 border-b border-zinc-200 flex items-center justify-between gap-3">
                <div className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">Son Olaylar</div>
                <div className="flex items-center gap-2">
                  <select
                    value={filterEvent} onChange={(e) => setFilterEvent(e.target.value)}
                    className="h-8 px-2 rounded-md border border-zinc-300 bg-white text-xs text-zinc-900"
                    data-testid="funnel-filter-event"
                  >
                    <option value="">Tüm olaylar</option>
                    {allowedEvents.map((e) => (<option key={e} value={e}>{EVENT_LABEL[e] || e}</option>))}
                  </select>
                  <input
                    type="text" value={filterTier} onChange={(e) => setFilterTier(e.target.value)}
                    placeholder="Tier (örn. LGS_SAY)"
                    className="h-8 px-2 rounded-md border border-zinc-300 bg-white text-xs text-zinc-900 w-40"
                    data-testid="funnel-filter-tier"
                  />
                </div>
              </div>
              {events.length === 0 ? (
                <div className="p-8 text-center text-xs text-zinc-500">Bu filtre için olay yok.</div>
              ) : (
                <div className="max-h-[500px] overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-zinc-50 sticky top-0">
                      <tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500 border-b border-zinc-100">
                        <th className="px-4 py-2 font-semibold">Zaman</th>
                        <th className="px-4 py-2 font-semibold">Olay</th>
                        <th className="px-4 py-2 font-semibold">Owner Key</th>
                        <th className="px-4 py-2 font-semibold">Tier</th>
                        <th className="px-4 py-2 font-semibold">Session</th>
                      </tr>
                    </thead>
                    <tbody>
                      {events.map((ev) => (
                        <tr key={ev.id} className="border-b border-zinc-50 last:border-0 hover:bg-zinc-50/50" data-testid={`funnel-row-${ev.id}`}>
                          <td className="px-4 py-2 text-xs text-zinc-500 font-mono">{fmtTs(ev.created_at)}</td>
                          <td className="px-4 py-2 text-xs text-zinc-900">{EVENT_LABEL[ev.event_type] || ev.event_type}</td>
                          <td className="px-4 py-2 font-mono text-[11px] text-zinc-500 truncate max-w-xs" title={ev.owner_key}>{ev.owner_key}</td>
                          <td className="px-4 py-2 text-xs text-zinc-700">{ev.payload?.tier || "—"}</td>
                          <td className="px-4 py-2 font-mono text-[11px] text-zinc-500 truncate max-w-xs" title={ev.payload?.session_id || ""}>
                            {ev.payload?.session_id || "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </AppShell>
  );
}
